import React, { useRef } from "react";

import { Loader2, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/src/components/ui/alert";
import { Button } from "@/src/components/ui/button";
import { useAppearancePreview } from "@/src/hooks/useAppearancePreview";
import { buildPreviewUrl } from "@/src/utils/appearancePreview";

import type { JSX } from "react";

import type { AppearancePreviewState } from "@/src/types/appearancePreview.types";

interface AppearancePreviewFrameProps {
  baseUrl: string;
  state: AppearancePreviewState | null;
}

function AppearancePreviewFrame({
  baseUrl,
  state,
}: AppearancePreviewFrameProps): JSX.Element {
  const { t } = useTranslation("editor-appearance");
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { status, handleLoad, reload } = useAppearancePreview({
    iframeRef,
    state,
  });

  return (
    <div className="relative size-full">
      <iframe
        ref={iframeRef}
        className="size-full border-0"
        onLoad={handleLoad}
        sandbox="allow-scripts allow-same-origin allow-forms"
        src={buildPreviewUrl(baseUrl)}
        title={t("previewFrameTitle")}
      />
      {status === "connecting" ? (
        <div
          className="bg-card text-muted-foreground pointer-events-none absolute top-3 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full border px-3 py-1.5 text-xs shadow-sm"
          role="status"
        >
          <Loader2 className="size-3.5 animate-spin" />
          {t("previewConnecting")}
        </div>
      ) : null}
      {status === "unavailable" ? (
        <Alert className="absolute inset-x-3 top-3 w-auto" variant="warning">
          <TriangleAlert />
          <AlertDescription className="flex items-center justify-between gap-3">
            {t("previewUnavailable")}
            <Button onClick={reload} type="button" variant="outline">
              {t("previewReload")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}

export default AppearancePreviewFrame;
